/**
 * RAG Page - Document Upload & Search
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  LinearProgress,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Description as FileIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import axios from 'axios';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';
import config from '../config';
import { getRagErrorMessage } from '../utils/errorHandler';

const API_URL = config.apiUrl;

export default function RAGPage() {
  const { user } = useAuthStore();
  const { t } = useTranslation(['common', 'rag']);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [department, setDepartment] = useState('');
  const [searchDepartment, setSearchDepartment] = useState('');
  const [departments, setDepartments] = useState([]);
  const [category, setCategory] = useState('general');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [allDocuments, setAllDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Get auth header helper
  const getAuthHeader = () => {
    const token = localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  // Only HR and SALES departments for document upload
  const ALLOWED_DEPARTMENTS = [
    { id: 1, name: 'HR', code: 'HR' },
    { id: 2, name: 'Sales', code: 'SALES' },
  ];

  // Initialize departments on mount
  useEffect(() => {
    setDepartments(ALLOWED_DEPARTMENTS);
    setDepartment('HR');
    setSearchDepartment('HR');
    loadAllDocuments();
  }, []);

  // Load all documents via backend
  const loadAllDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/rag/documents`, {
        headers: getAuthHeader(),
      });
      setAllDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const allowedTypes = [
      'application/pdf',
      'text/markdown',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (allowedTypes.includes(file.type) || file.name.endsWith('.md') || file.name.endsWith('.txt')) {
      setSelectedFile(file);
      setUploadStatus(null);
    } else {
      setUploadStatus({
        type: 'error',
        message: t('notifications.invalidFile', { ns: 'rag' }),
      });
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus({ type: 'error', message: t('notifications.noFile', { ns: 'rag' }) });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('department_code', department);
    formData.append('category', category);

    try {
      const response = await axios.post(`${API_URL}/api/v1/rag/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      const isQueued = response.data.status === 'queued' || response.status === 202;
      
      setUploadStatus({
        type: isQueued ? 'info' : 'success',
        message: isQueued 
          ? t('notifications.uploadingBackground', { ns: 'rag', id: response.data.document_id })
          : t('notifications.uploadSuccess', { ns: 'rag',
              id: response.data.document_id,
            }),
        data: response.data,
      });

      setUploadedDocs((prev) => [
        ...prev,
        {
          filename: selectedFile.name,
          department,
          category,
          chunks: response.data.chunks_created,
          status: response.data.status,
          timestamp: new Date().toLocaleString(),
        },
      ]);

      setSelectedFile(null);
      
      // Reload documents list immediately
      loadAllDocuments();
      
      // If queued, auto-refresh after 5 seconds
      if (isQueued) {
        setTimeout(() => {
          loadAllDocuments();
        }, 5000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        type: 'error',
        message: getRagErrorMessage(error, t),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    setSearching(true);
    setSearchResults(null);

    try {
      const response = await axios.post(`${API_URL}/api/v1/rag/query`, {
        query: searchQuery,
        department_code: searchDepartment,
        top_k: 5,
      }, {
        headers: getAuthHeader(),
      });

      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults({
        error: t('errors.rag.searchFailed'),
      });
    } finally {
      setSearching(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return t('common.na');
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDeleteClick = (doc) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/rag/documents/${documentToDelete.doc_id}`, {
        headers: getAuthHeader(),
      });
      
      setUploadStatus({
        type: 'success',
        message: t('notifications.deleteSuccess', { ns: 'rag',
          filename: documentToDelete.filename,
        }),
      });
      
      // Reload documents list
      loadAllDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      setUploadStatus({
        type: 'error',
        message: getRagErrorMessage(error, t),
      });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: '#f5f7fa',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          📚 {t('header.title', { ns: 'rag' })}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {t('header.subtitle', { ns: 'rag' })}
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Upload Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#667eea' }}>
                  📤 {t('upload.title', { ns: 'rag' })}
                </Typography>

                {/* Department Selection */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('upload.departmentLabel', { ns: 'rag' })}</InputLabel>
                  <Select
                    value={department}
                    label={t('upload.departmentLabel', { ns: 'rag' })}
                    onChange={(e) => setDepartment(e.target.value)}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.code}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Category Input */}
                <TextField
                  fullWidth
                  label={t('upload.categoryLabel', { ns: 'rag' })}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder={t('upload.categoryPlaceholder', { ns: 'rag' })}
                  sx={{ mb: 3 }}
                />

                {/* Drag & Drop Zone */}
                <Paper
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '3px dashed',
                    borderColor: dragActive
                      ? '#667eea'
                      : selectedFile
                      ? '#4caf50'
                      : '#d0d0d0',
                    borderRadius: 2,
                    bgcolor: dragActive
                      ? '#e8ebff'
                      : selectedFile
                      ? '#f1f8f4'
                      : '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    mb: 2,
                    '&:hover': {
                      borderColor: '#667eea',
                      bgcolor: '#f0f0ff',
                    },
                  }}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    hidden
                    accept=".pdf,.md,.txt,.doc,.docx"
                    onChange={handleFileInputChange}
                  />

                  {selectedFile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                      <FileIcon sx={{ fontSize: 48, color: '#4caf50' }} />
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(selectedFile.size)}
                        </Typography>
                      </Box>
                      <CheckIcon sx={{ fontSize: 32, color: '#4caf50' }} />
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        {t('upload.dropzone.title', { ns: 'rag' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {t('upload.dropzone.supportedHint', { ns: 'rag' })}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Upload Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  startIcon={uploading ? null : <UploadIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  {uploading ? t('upload.uploading', { ns: 'rag' }) : t('upload.button', { ns: 'rag' })}
                </Button>

                {uploading && <LinearProgress sx={{ mt: 2 }} />}

                {/* Upload Status */}
                {uploadStatus && (
                  <Alert severity={uploadStatus.type} sx={{ mt: 2 }}>
                    {uploadStatus.message}
                    {uploadStatus.data && uploadStatus.data.chunks_created > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('upload.chunksCreated', { ns: 'rag', count: uploadStatus.data.chunks_created })}
                      </Typography>
                    )}
                    {uploadStatus.data && uploadStatus.data.status === 'queued' && (
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                        {t('status.queued', { ns: 'rag', status: uploadStatus.data.status })}
                      </Typography>
                    )}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Search Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#667eea' }}>
                  🔍 {t('search.title', { ns: 'rag' })}
                </Typography>

                {/* Department Selection for Search */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('search.departmentLabel', { ns: 'rag' })}</InputLabel>
                  <Select
                    value={searchDepartment}
                    label={t('search.departmentLabel', { ns: 'rag' })}
                    onChange={(e) => setSearchDepartment(e.target.value)}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.code}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Search Bar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={t('search.placeholder', { ns: 'rag' })}
                    disabled={searching}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={searching || !searchQuery.trim()}
                    startIcon={!searching ? <SearchIcon /> : null}
                    sx={{
                      minWidth: 120,
                      bgcolor: '#667eea',
                      '&:hover': { bgcolor: '#5568d3' },
                    }}
                  >
                    {searching ? t('search.searching', { ns: 'rag' }) : t('search.button', { ns: 'rag' })}
                  </Button>
                </Box>

                {searching && <LinearProgress sx={{ mb: 2 }} />}

                {/* Search Results */}
                {searchResults && (
                  <Box>
                    {searchResults.error ? (
                      <Alert severity="error">{searchResults.error}</Alert>
                    ) : (
                      <>
                        {/* Answer Box */}
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            mb: 2,
                            background: 'linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)',
                            borderLeft: '4px solid #667eea',
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                            💡 {t('search.answerTitle', { ns: 'rag' })}
                          </Typography>
                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                            {searchResults.answer || t('search.noAnswer', { ns: 'rag' })}
                          </Typography>
                        </Paper>

                        {/* Sources */}
                        {searchResults.sources && searchResults.sources.length > 0 && (
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                              📑 {t('search.sourcesTitle', { ns: 'rag', count: searchResults.sources.length })}
                            </Typography>
                            {searchResults.sources.map((source, idx) => (
                              <Paper
                                key={idx}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  mb: 1.5,
                                  bgcolor: '#f9f9f9',
                                  borderLeft: '3px solid #667eea',
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <Chip
                                    label={t('search.scoreLabel', { ns: 'rag',
                                      score:
                                        source.similarity != null
                                          ? (source.similarity * 100).toFixed(1)
                                          : t('common.na'),
                                    })}
                                    size="small"
                                    sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 600 }}
                                  />
                                  <Chip
                                    label={source.metadata?.category || t('common.na')}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                  {source.content}
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Document Management Section */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                    <FolderIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {t('documents.title', { ns: 'rag' })}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={loadAllDocuments}
                    disabled={loadingDocuments}
                  >
                    {t('documents.refresh', { ns: 'rag' })}
                  </Button>
                </Box>

                {loadingDocuments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : allDocuments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                    <FileIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                    <Typography variant="body1">{t('documents.empty', { ns: 'rag' })}</Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} elevation={0} sx={{ bgcolor: '#fafafa' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.filename', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.department', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.type', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.size', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.chunks', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.status', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('documents.table.uploaded', { ns: 'rag' })}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="center">
                            {t('documents.table.actions', { ns: 'rag' })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allDocuments.map((doc) => (
                          <TableRow
                            key={doc.doc_id}
                            sx={{
                              '&:hover': { bgcolor: '#f5f5f5' },
                              transition: 'background-color 0.2s',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FileIcon sx={{ color: '#667eea' }} />
                                <Tooltip title={doc.filename}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {doc.filename}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.department_name || doc.department_code}
                                size="small"
                                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {doc.file_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatFileSize(doc.file_size)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={doc.chunk_count} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.status}
                                size="small"
                                color={doc.status === 'completed' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(doc.uploaded_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={t('documents.actions.delete', { ns: 'rag' })}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(doc)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Uploads */}
          {uploadedDocs.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                      📋 {t('upload.recentTitle', { ns: 'rag' })}
                    </Typography>
                    <IconButton onClick={() => setUploadedDocs([])} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    {uploadedDocs
                      .slice()
                      .reverse()
                      .map((doc, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: '#f9f9f9',
                              borderRadius: 2,
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                bgcolor: '#f0f0f0',
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                              <FileIcon sx={{ fontSize: 32, color: '#667eea' }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, mb: 0.5, wordBreak: 'break-word' }}
                                >
                                  {doc.filename}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {t('upload.recentMeta', { ns: 'rag',
                                    department: doc.department,
                                    category: doc.category,
                                  })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {doc.status === 'queued' 
                                    ? t('status.processing', { ns: 'rag', timestamp: doc.timestamp })
                                    : t('upload.recentChunks', { ns: 'rag',
                                        chunks: doc.chunks,
                                        timestamp: doc.timestamp,
                                      })}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 700 }}>
          ⚠️ {t('delete.title', { ns: 'rag' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('delete.prompt', { ns: 'rag' })}
          </DialogContentText>
          {documentToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {documentToDelete.filename}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('delete.details.department', { ns: 'rag',
                  department: documentToDelete.department_name || documentToDelete.department_code,
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('delete.details.chunks', { ns: 'rag', count: documentToDelete.chunk_count })}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('delete.warning', { ns: 'rag' })}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('delete.cancel', { ns: 'rag' })}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={!deleting ? <DeleteIcon /> : null}
          >
            {deleting ? t('delete.deleting', { ns: 'rag' }) : t('delete.confirm', { ns: 'rag' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

